import { useEffect, useRef, useState } from "react";
import { useTheme } from "styled-components";
import { useStateValue } from "providers";
import { useGetQuery, usePostQuery } from "hooks";
import { SearchTypeahead } from "components/forms";
import { getAdminDungeon } from "actions/api/admin/dungeons";
import {
  QUEST_STEP_COLLECT_ITEM,
  QUEST_STEP_DELIVER_TO_NPC,
  QUEST_STEP_PLACE_ON_TILE,
} from "../../config/constants";
import {
  Page,
  Header,
  Title,
  Body,
  QuestList,
  QuestItem,
  Detail,
  Field,
  Label,
  StatusBar,
} from "./styles";

const STEP_TYPES = [
  { value: QUEST_STEP_COLLECT_ITEM, label: "Collect Item" },
  { value: QUEST_STEP_DELIVER_TO_NPC, label: "Deliver to NPC" },
  { value: QUEST_STEP_PLACE_ON_TILE, label: "Place on Tile" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const blank = () => ({
  _new: true,
  id: `quest_${Date.now()}`,
  name: "",
  description: "",
  giverNpcPlacementId: null,
  steps: [],
  award: { xp: 0, gold: 0 },
  dialog: { intro: [], in_progress: [], complete: [] },
});

const blankStep = (type) => ({
  id: `step_${Date.now()}_${Math.random().toString(36).slice(2)}`,
  type,
  description: "",
  dungeon_slug: "",
  ...(type === QUEST_STEP_COLLECT_ITEM && { itemEntityId: "" }),
  ...(type === QUEST_STEP_DELIVER_TO_NPC && { itemType: "gold", targetNpcPlacementId: "" }),
  ...(type === QUEST_STEP_PLACE_ON_TILE && { itemType: "gold", tileX: 0, tileY: 0 }),
});

// ── NpcPlacementTypeahead ─────────────────────────────────────────────────────

const npcPlacementLabel = (p) => `${p.Npc?.name} — ${p.Dungeon?.slug}`;

const NpcPlacementTypeahead = ({ value, npcPlacements, inp, c, onSelect }) => (
  <SearchTypeahead
    value={value}
    items={npcPlacements}
    getLabel={npcPlacementLabel}
    renderItem={(p) => (<>{p.Npc?.name} <span style={{ opacity: 0.5 }}>— {p.Dungeon?.slug}</span></>)}
    onSelect={onSelect}
    placeholder="Search NPC name…"
    inputStyle={inp}
    dropdownStyle={{ background: c.bg, border: `1px solid ${c.border}` }}
    itemStyle={{ padding: "0.3rem 0.5rem", cursor: "pointer", fontSize: "0.68rem", fontFamily: "Silkscreen, monospace", color: c.text, borderBottom: `1px solid ${c.inner}` }}
  />
);

// ── ItemTypeahead — per-step component so each step has isolated input state ──

const itemLabel = (i) => `${i.name}  L${i.level + 1} (${i.x},${i.y})`;

const ItemTypeahead = ({ value, items, loading, smallInp, c, onSelect }) => (
  <SearchTypeahead
    value={value}
    items={items}
    getLabel={itemLabel}
    renderItem={(i) => (<>{i.name} <span style={{ opacity: 0.5 }}>L{i.level + 1} ({i.x},{i.y})</span></>)}
    onSelect={onSelect}
    placeholder={loading ? "Loading…" : items.length ? "Search item…" : "Set dungeon slug first"}
    inputStyle={smallInp}
    dropdownStyle={{ background: c.bg, border: `1px solid ${c.border}`, maxHeight: 180 }}
    itemStyle={{ padding: "0.25rem 0.5rem", cursor: "pointer", fontSize: "0.62rem", fontFamily: "Silkscreen, monospace", color: c.text, borderBottom: `1px solid ${c.inner}` }}
  />
);

// ── Component ─────────────────────────────────────────────────────────────────

export const QuestEditor = () => {
  const [{ session }] = useStateValue();
  const { colors } = useTheme();
  const isAdmin = session?.user?.roles?.some((r) => r.slug === "admin");
  const base = isAdmin ? "/admin" : "/host";

  const { post: postCreate } = usePostQuery({ method: "POST" });
  const { post: postUpdate } = usePostQuery({ method: "PUT" });
  const { post: postDelete } = usePostQuery({ method: "DELETE" });

  const [quests, setQuests] = useState([]);
  const [selected, setSelected] = useState(null); // id of selected quest
  const [status, setStatus] = useState(null); // { ok, msg }
  const deletedRef = useRef([]);

  const c = {
    text: colors("highlight"),
    muted: colors("highlight", 0.55),
    border: colors("highlight", 0.3),
    inner: colors("highlight", 0.15),
    bg: colors("background"),
    danger: colors("danger"),
  };

  const inp = {
    background: c.bg,
    border: `1px solid ${c.border}`,
    color: c.text,
    padding: "0.3rem 0.5rem",
    fontFamily: "Silkscreen, monospace",
    fontSize: "0.7rem",
    width: "100%",
    boxSizing: "border-box",
  };

  const smallInp = { ...inp, fontSize: "0.62rem" };

  // Load quests
  const { data: questsData } = useGetQuery({ url: `${base}/quests`, enabled: true }) ?? {};
  useEffect(() => {
    if (!questsData?.quests) return;
    setQuests(
      questsData.quests.map((q) => ({
        id: q.id,
        name: q.name,
        description: q.description ?? "",
        giverNpcPlacementId: q.giver_npc_placement_id ?? null,
        steps: q.steps ?? [],
        award: q.award ?? { xp: 0, gold: 0 },
        dialog: {
          intro: q.dialog?.intro ?? [],
          in_progress: q.dialog?.in_progress ?? [],
          complete: q.dialog?.complete ?? [],
        },
      }))
    );
  }, [questsData]);

  // Load all dungeons (for slug suggestions)
  const { data: dungeonsData } = useGetQuery({ url: `${base}/dungeons`, enabled: true }) ?? {};
  const dungeons = dungeonsData?.dungeons?.rows ?? dungeonsData?.dungeons ?? [];

  // Load all NPC placements for giver typeahead
  const { data: placementsData } = useGetQuery({ url: `${base}/npc_placements`, enabled: true }) ?? {};
  const npcPlacements = placementsData?.npc_placements ?? [];

  // Cache of dungeon items by slug: null = loading, [] = loaded (empty), [...] = loaded
  const [dungeonItemCache, setDungeonItemCache] = useState({});

  const quest = quests.find((q) => q.id === selected) ?? null;

  // Load item entities for any dungeon slug referenced in collect_item steps
  useEffect(() => {
    if (!quest) return;
    const slugs = [...new Set(
      quest.steps
        .filter((s) => s.type === QUEST_STEP_COLLECT_ITEM && s.dungeon_slug)
        .map((s) => s.dungeon_slug)
    )];
    slugs.forEach((slug) => {
      if (dungeonItemCache[slug] !== undefined) return;
      setDungeonItemCache((prev) => ({ ...prev, [slug]: null }));
      getAdminDungeon(slug).then((res) => {
        const items = [];
        (res?.dungeon?.map_data?.levels ?? []).forEach((lv, li) => {
          (lv.entities ?? []).forEach((e) => {
            if (e.entityType === 'item' || e.entityType === 'treasure') {
              items.push({ id: e.id, name: e.name || e.treasureType || 'item', level: li, x: e.x, y: e.y });
            }
          });
        });
        setDungeonItemCache((prev) => ({ ...prev, [slug]: items }));
      }).catch(() => setDungeonItemCache((prev) => ({ ...prev, [slug]: [] })));
    });
  }, [quest?.id, quest?.steps]);

  const updateQuest = (changes) =>
    setQuests((prev) => prev.map((q) => q.id === selected ? { ...q, ...changes } : q));

  const addStep = (type) => {
    const step = blankStep(type);
    updateQuest({ steps: [...(quest?.steps ?? []), step] });
  };

  const updateStep = (sid, changes) =>
    updateQuest({ steps: quest.steps.map((s) => s.id === sid ? { ...s, ...changes } : s) });

  const removeStep = (sid) =>
    updateQuest({ steps: quest.steps.filter((s) => s.id !== sid) });

  const newQuest = () => {
    const q = blank();
    setQuests((prev) => [...prev, q]);
    setSelected(q.id);
  };

  const deleteSelected = async () => {
    if (!quest || !window.confirm(`Delete "${quest.name || "this quest"}"?`)) return;
    if (!quest._new) {
      await postDelete({}, `${base}/quests/${quest.id}`);
    }
    setQuests((prev) => prev.filter((q) => q.id !== selected));
    setSelected(null);
  };

  const save = async () => {
    if (!quest) return;
    setStatus(null);
    const payload = {
      name: quest.name,
      description: quest.description,
      steps: quest.steps,
      award: quest.award,
      dialog: quest.dialog ?? {},
      giver_npc_placement_id: quest.giverNpcPlacementId || null,
    };
    if (quest._new) {
      const res = await postCreate(payload, `${base}/quests`);
      if (!res) { setStatus({ ok: false, msg: "Save failed" }); return; }
      if (res?.quest?.id) {
        setQuests((prev) => prev.map((q) => q.id === selected
          ? { ...res.quest, giverNpcPlacementId: res.quest.giver_npc_placement_id ?? null, steps: res.quest.steps ?? [], award: res.quest.award ?? { xp: 0, gold: 0 }, dialog: { intro: res.quest.dialog?.intro ?? [], in_progress: res.quest.dialog?.in_progress ?? [], complete: res.quest.dialog?.complete ?? [] } }
          : q));
        setSelected(res.quest.id);
      }
    } else {
      const res = await postUpdate(payload, `${base}/quests/${quest.id}`);
      if (!res) { setStatus({ ok: false, msg: "Save failed" }); return; }
    }
    setStatus({ ok: true, msg: "Saved" });
    setTimeout(() => setStatus(null), 2500);
  };

  return (
    <Page>
      <Header>
        <Title>Quest Editor</Title>
        <button
          onClick={newQuest}
          style={{ ...smallInp, width: "auto", cursor: "pointer", padding: "0.3rem 0.75rem" }}
        >
          + New Quest
        </button>
        {quest && (
          <>
            <button
              onClick={save}
              style={{ ...smallInp, width: "auto", cursor: "pointer", padding: "0.3rem 0.75rem", borderColor: c.text }}
            >
              Save
            </button>
            <button
              onClick={deleteSelected}
              style={{ ...smallInp, width: "auto", cursor: "pointer", padding: "0.3rem 0.75rem", borderColor: c.danger, color: c.danger }}
            >
              Delete
            </button>
          </>
        )}
        {status && <StatusBar $err={!status.ok}>{status.msg}</StatusBar>}
      </Header>

      <Body>
        <QuestList>
          {quests.length === 0 && (
            <div style={{ color: c.muted, fontSize: "0.65rem", padding: "0.4rem" }}>No quests yet.</div>
          )}
          {quests.map((q) => (
            <QuestItem key={q.id} $active={q.id === selected} onClick={() => setSelected(q.id)}>
              {q.name || <span style={{ opacity: 0.4 }}>Untitled</span>}
              {q._new && <span style={{ opacity: 0.4, fontSize: "0.55rem" }}> (unsaved)</span>}
            </QuestItem>
          ))}
        </QuestList>

        <Detail>
          {!quest && (
            <div style={{ color: c.muted, fontSize: "0.75rem" }}>Select a quest or create a new one.</div>
          )}
          {quest && (
            <>
              <Field>
                <Label>Name</Label>
                <input value={quest.name} onChange={(e) => updateQuest({ name: e.target.value })} style={inp} />
              </Field>

              <Field>
                <Label>Description</Label>
                <textarea
                  value={quest.description}
                  onChange={(e) => updateQuest({ description: e.target.value })}
                  rows={3}
                  style={{ ...inp, resize: "vertical" }}
                />
              </Field>

              <Field>
                <Label>Giver NPC</Label>
                <NpcPlacementTypeahead
                  value={quest.giverNpcPlacementId}
                  npcPlacements={npcPlacements}
                  inp={inp}
                  c={c}
                  onSelect={(id) => updateQuest({ giverNpcPlacementId: id })}
                />
              </Field>

              <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
                <Field style={{ flex: 1, marginBottom: 0 }}>
                  <Label>XP Award</Label>
                  <input
                    type="number"
                    min={0}
                    value={quest.award?.xp ?? 0}
                    onChange={(e) => updateQuest({ award: { ...quest.award, xp: Number(e.target.value) } })}
                    style={inp}
                  />
                </Field>
                <Field style={{ flex: 1, marginBottom: 0 }}>
                  <Label>Gold Award</Label>
                  <input
                    type="number"
                    min={0}
                    value={quest.award?.gold ?? 0}
                    onChange={(e) => updateQuest({ award: { ...quest.award, gold: Number(e.target.value) } })}
                    style={inp}
                  />
                </Field>
              </div>

              {[
                { key: "intro",       label: "Intro dialog",       hint: "Shown when player accepts quest" },
                { key: "in_progress", label: "In-progress dialog", hint: "Shown while quest is active" },
                { key: "complete",    label: "Complete dialog",     hint: "Shown when all steps done" },
              ].map(({ key, label, hint }) => (
                <div key={key} style={{ borderTop: `1px solid ${c.inner}`, marginBottom: "1rem", paddingTop: "1rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
                    <Label style={{ margin: 0 }}>{label}</Label>
                    <span style={{ fontSize: "0.55rem", color: c.muted }}>{hint}</span>
                    <button
                      onClick={() => updateQuest({ dialog: { ...quest.dialog, [key]: [...(quest.dialog?.[key] ?? []), ""] } })}
                      style={{ ...smallInp, width: "auto", cursor: "pointer", padding: "0.15rem 0.5rem", marginLeft: "auto" }}
                    >+ Line</button>
                  </div>
                  {(quest.dialog?.[key] ?? []).length === 0 && (
                    <div style={{ fontSize: "0.6rem", color: c.muted, fontStyle: "italic" }}>Auto-generated (add lines to override)</div>
                  )}
                  {(quest.dialog?.[key] ?? []).map((line, li) => (
                    <div key={li} style={{ display: "flex", gap: "0.4rem", marginBottom: "0.3rem" }}>
                      <input
                        value={line}
                        onChange={(e) => {
                          const lines = [...(quest.dialog?.[key] ?? [])];
                          lines[li] = e.target.value;
                          updateQuest({ dialog: { ...quest.dialog, [key]: lines } });
                        }}
                        style={{ ...smallInp, flex: 1 }}
                      />
                      <button
                        onClick={() => {
                          const lines = (quest.dialog?.[key] ?? []).filter((_, i) => i !== li);
                          updateQuest({ dialog: { ...quest.dialog, [key]: lines } });
                        }}
                        style={{ background: "none", border: "none", color: c.danger, cursor: "pointer", fontFamily: "Silkscreen, monospace" }}
                      >✕</button>
                    </div>
                  ))}
                </div>
              ))}

              <div style={{ borderTop: `1px solid ${c.inner}`, marginBottom: "1rem", paddingTop: "1rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
                  <Label style={{ margin: 0 }}>Steps ({quest.steps.length})</Label>
                  {STEP_TYPES.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => addStep(t.value)}
                      style={{ ...smallInp, width: "auto", cursor: "pointer", padding: "0.2rem 0.5rem" }}
                    >
                      + {t.label}
                    </button>
                  ))}
                </div>

                {quest.steps.map((step, si) => (
                  <div
                    key={step.id}
                    style={{
                      marginBottom: "0.75rem",
                      padding: "0.6rem 0.75rem",
                      border: `1px solid ${c.inner}`,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem" }}>
                      <span style={{ color: c.muted, fontSize: "0.55rem" }}>{si + 1}.</span>
                      <select
                        value={step.type}
                        onChange={(e) => updateStep(step.id, { type: e.target.value })}
                        style={{ ...smallInp, width: "auto", flex: 1 }}
                      >
                        {STEP_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => removeStep(step.id)}
                        style={{ background: "none", border: "none", color: c.danger, cursor: "pointer", fontFamily: "Silkscreen, monospace", fontSize: "0.8rem" }}
                      >
                        ✕
                      </button>
                    </div>

                    <Label>Description</Label>
                    <input
                      value={step.description}
                      onChange={(e) => updateStep(step.id, { description: e.target.value })}
                      placeholder="What the player sees"
                      style={{ ...smallInp, marginBottom: "0.4rem" }}
                    />

                    <Label>Dungeon Slug</Label>
                    <input
                      value={step.dungeon_slug ?? ""}
                      onChange={(e) => updateStep(step.id, { dungeon_slug: e.target.value })}
                      placeholder="which dungeon this step applies to"
                      list={`dungeon-slugs-${step.id}`}
                      style={{ ...smallInp, marginBottom: "0.4rem" }}
                    />
                    <datalist id={`dungeon-slugs-${step.id}`}>
                      {dungeons.map((d) => <option key={d.slug} value={d.slug} />)}
                    </datalist>

                    {step.type === QUEST_STEP_COLLECT_ITEM && (
                      <>
                        <Label>Item</Label>
                        <ItemTypeahead
                          value={step.itemEntityId ?? ""}
                          items={dungeonItemCache[step.dungeon_slug] ?? []}
                          loading={step.dungeon_slug && dungeonItemCache[step.dungeon_slug] === null}
                          smallInp={smallInp}
                          c={c}
                          onSelect={(id) => updateStep(step.id, { itemEntityId: id })}
                        />
                      </>
                    )}

                    {step.type === QUEST_STEP_DELIVER_TO_NPC && (
                      <>
                        <Label>Item Type</Label>
                        <input
                          value={step.itemType ?? ""}
                          onChange={(e) => updateStep(step.id, { itemType: e.target.value })}
                          placeholder="e.g. gold, potion"
                          style={{ ...smallInp, marginBottom: "0.4rem" }}
                        />
                        <Label>Target NPC</Label>
                        <NpcPlacementTypeahead
                          value={step.targetNpcPlacementId ?? ""}
                          npcPlacements={npcPlacements}
                          inp={smallInp}
                          c={c}
                          onSelect={(id) => updateStep(step.id, { targetNpcPlacementId: id })}
                        />
                      </>
                    )}

                    {step.type === QUEST_STEP_PLACE_ON_TILE && (
                      <>
                        <Label>Item Type</Label>
                        <input
                          value={step.itemType ?? ""}
                          onChange={(e) => updateStep(step.id, { itemType: e.target.value })}
                          placeholder="e.g. gold, potion"
                          style={{ ...smallInp, marginBottom: "0.4rem" }}
                        />
                        <div style={{ display: "flex", gap: "0.75rem" }}>
                          <div style={{ flex: 1 }}>
                            <Label>Tile X (col)</Label>
                            <input
                              type="number"
                              min={0}
                              value={step.tileX ?? 0}
                              onChange={(e) => updateStep(step.id, { tileX: Number(e.target.value) })}
                              style={smallInp}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <Label>Tile Y (row)</Label>
                            <input
                              type="number"
                              min={0}
                              value={step.tileY ?? 0}
                              onChange={(e) => updateStep(step.id, { tileY: Number(e.target.value) })}
                              style={smallInp}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </Detail>
      </Body>
    </Page>
  );
};
